import { normalizePatientHeightCm, normalizePatientWeightKg, toFiniteNumber } from '../utils/patientMetrics';
import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';

export type PeripheralMuscleStrengthKey =
  | 'pectoralisMajor'
  | 'latissimusDorsi'
  | 'deltoid'
  | 'tricepsBrachii'
  | 'bicepsBrachii'
  | 'quadricepsFemoris';

export type PeripheralMuscleStrengthSide = 'right' | 'left';
export type PeripheralForceUnit = 'kgf' | 'N';

export type PeripheralMuscleStrengthPatientContext = {
  birthDate?: string | null;
  ageYears?: number | null;
  sex?: string | null;
  weightKg?: number | null;
  heightCm?: number | null;
  identifier?: string | null;
};

export type PeripheralMuscleStrengthMeasurement = {
  attemptsN: number[];
  bestN: number | null;
  bestKgf: number | null;
  predictedN: number | null;
  predictedKgf: number | null;
  percentPredicted: number | null;
  topTwoDiffPercent: number | null;
  reproducible: boolean;
};

export type PeripheralMuscleStrengthResultPayload = {
  unit: PeripheralForceUnit;
  dominantSide: PeripheralMuscleStrengthSide;
  bmi: number | null;
  selectedMuscles: PeripheralMuscleStrengthKey[];
  muscles: Record<PeripheralMuscleStrengthKey, PeripheralMuscleStrengthMeasurement>;
  summary: {
    selectedMusclesCount: number;
    completedMuscles: number;
    reproducibleMuscles: number;
    allMusclesCompleted: boolean;
    allMusclesReproducible: boolean;
    averagePercentPredicted: number | null;
    lowestPercentPredicted: number | null;
    lowestMuscleKey: PeripheralMuscleStrengthKey | null;
    lowestMuscleLabel: string | null;
  };
  protocol: {
    source: 'Garcia2024';
    citation: string;
    supplementLabel: string;
    supplier: string;
    dominantSideOnly: boolean;
    contractionSeconds: number;
    minAttempts: number;
    maxAttempts: number;
    reproducibilityThresholdPercent: number;
  };
  notes?: string;
  finishedAt?: string;
  patientContext?: PeripheralMuscleStrengthPatientContext | null;
  professionalContext?: {
    name?: string | null;
    registration?: string | null;
    role?: string | null;
  } | null;
};

type PeripheralMuscleStrengthBuildInput = {
  unit: PeripheralForceUnit;
  dominantSide: PeripheralMuscleStrengthSide;
  selectedMuscles: PeripheralMuscleStrengthKey[];
  attemptsByMuscle: Partial<Record<PeripheralMuscleStrengthKey, number[]>>;
  notes?: string;
  patientContext?: PeripheralMuscleStrengthPatientContext | null;
  finishedAt?: string;
};

type EquationContext = {
  ageYears: number | null;
  sexBinary: 0 | 1 | null;
  bmi: number | null;
};

type PeripheralMuscleStrengthDefinition = {
  key: PeripheralMuscleStrengthKey;
  label: string;
  shortLabel: string;
  equationLabel: string;
  description: string[];
  predictN: (context: EquationContext) => number | null;
};

const NEWTONS_PER_KGF = 9.80665;
const ROUNDING_FORCE_DECIMALS = 1;
const ROUNDING_PERCENT_DECIMALS = 1;

export const PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL = {
  source: 'Garcia2024' as const,
  citation: 'Garcia T et al. Arch Phys Med Rehabil. 2024;105(6):1099-1105.',
  supplementLabel: 'E-Table 1 - Peripheral muscle strength equations',
  supplier: 'Traction/compression dynamometer 200kgf; EMG System',
  dominantSideOnly: true,
  contractionSeconds: 6,
  minAttempts: 3,
  maxAttempts: 15,
  reproducibilityThresholdPercent: 5,
};

const round = (value: number | null | undefined, decimals = 1): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Number(value.toFixed(decimals));
};

const resolvePeripheralSex = (sex?: string | null): 'M' | 'F' | null => {
  const normalized = String(sex || '').trim().toUpperCase();
  if (!normalized) return null;
  if (normalized.startsWith('F')) return 'F';
  if (normalized.startsWith('M')) return 'M';
  return null;
};

const resolveSexBinary = (sex?: string | null): 0 | 1 | null => {
  const normalized = resolvePeripheralSex(sex);
  if (normalized === 'F') return 1;
  if (normalized === 'M') return 0;
  return null;
};

const calculateBmi = (weightKg?: number | null, heightCm?: number | null): number | null => {
  const normalizedWeightKg = normalizePatientWeightKg(weightKg);
  const normalizedHeightCm = normalizePatientHeightCm(heightCm);
  if (normalizedWeightKg == null || normalizedHeightCm == null) return null;

  const heightM = normalizedHeightCm / 100;
  if (heightM <= 0) return null;

  return round(normalizedWeightKg / (heightM * heightM), 2);
};

export const convertForceValue = (value: number | null | undefined, from: PeripheralForceUnit, to: PeripheralForceUnit): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
  if (from === to) return value;
  if (from === 'kgf') return value * NEWTONS_PER_KGF;
  return value / NEWTONS_PER_KGF;
};

const normalizeAttemptsToNewtons = (values: unknown, unit: PeripheralForceUnit): number[] =>
  Array.isArray(values)
    ? values
        .map((value) => toFiniteNumber(value))
        .map((value) => convertForceValue(value, unit, 'N'))
        .filter((value): value is number => value != null && value > 0)
    : [];

const computeMeasurement = (
  attemptsN: number[],
  predictN: (context: EquationContext) => number | null,
  context: EquationContext,
): PeripheralMuscleStrengthMeasurement => {
  const ordered = [...attemptsN].sort((left, right) => right - left);
  const bestN = ordered[0] ?? null;
  const secondBestN = ordered[1] ?? null;
  const topTwoDiffPercent =
    bestN != null && secondBestN != null && bestN > 0 ? round((Math.abs(bestN - secondBestN) / bestN) * 100, ROUNDING_PERCENT_DECIMALS) : null;
  const predictedN = round(predictN(context), ROUNDING_FORCE_DECIMALS);
  const percentPredicted =
    bestN != null && predictedN != null && predictedN > 0 ? round((bestN / predictedN) * 100, ROUNDING_PERCENT_DECIMALS) : null;

  return {
    attemptsN: ordered.map((value) => Number(value.toFixed(ROUNDING_FORCE_DECIMALS))),
    bestN,
    bestKgf: round(convertForceValue(bestN, 'N', 'kgf'), ROUNDING_FORCE_DECIMALS),
    predictedN,
    predictedKgf: round(convertForceValue(predictedN, 'N', 'kgf'), ROUNDING_FORCE_DECIMALS),
    percentPredicted,
    topTwoDiffPercent,
    reproducible:
      attemptsN.length >= PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL.minAttempts &&
      topTwoDiffPercent != null &&
      topTwoDiffPercent <= PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL.reproducibilityThresholdPercent,
  };
};

export const PERIPHERAL_MUSCLE_STRENGTH_DEFINITIONS: PeripheralMuscleStrengthDefinition[] = [
  {
    key: 'pectoralisMajor',
    label: 'Pectoralis major',
    shortLabel: 'Peitoral maior',
    equationLabel: '143.53 - (67.54 x SEX)',
    description: [
      'Paciente em decúbito dorsal, com avaliação apenas do lado dominante e contração isométrica de 6 segundos.',
      'Ação principal: adução horizontal do ombro contra o dinamômetro.',
      'Porção clavicular: ombro em 90 graus de flexão, leve adução horizontal e rotação interna; resistência no antebraço proximal em direção à abdução horizontal.',
      'Porção esternocostal: braço abduzido a 150 graus na diagonal superior, cotovelo estendido; resistência na porção distal do braço em direção à abdução lateral.',
      'Mantenha a mesma variação do teste nas reavaliações.',
    ],
    predictN: ({ sexBinary }) => (sexBinary == null ? null : 143.53 - 67.54 * sexBinary),
  },
  {
    key: 'latissimusDorsi',
    label: 'Latissimus dorsi',
    shortLabel: 'Grande dorsal',
    equationLabel: '122.4 - (0.55 x AGE) - (33.95 x SEX)',
    description: [
      'Paciente em decúbito ventral, com o braço do lado testado em adução, extensão e rotação medial, e cotovelo estendido.',
      'Solicite adução e extensão do braço em direção ao quadril, mantendo a rotação medial.',
      'A resistência é aplicada no antebraço, próximo ao punho, no sentido oposto ao movimento: abdução com leve flexão.',
      'Use o lado dominante e sustente a CIVM por 6 segundos.',
    ],
    predictN: ({ ageYears, sexBinary }) =>
      ageYears == null || sexBinary == null ? null : 122.4 - 0.55 * ageYears - 33.95 * sexBinary,
  },
  {
    key: 'deltoid',
    label: 'Deltoide',
    shortLabel: 'Deltoide',
    equationLabel: '107.24 + (2.17 x BMI) - (66.72 x SEX)',
    description: [
      'Paciente sentado; escolha e padronize a mesma variação do deltoide para acompanhamento.',
      'Deltoide anterior: braço a 90 graus, levemente à frente do plano frontal; ação de flexão com resistência para trás e para baixo.',
      'Deltoide médio: braço abduzido a 90 graus no plano frontal; resistência no úmero distal em direção à adução.',
      'Deltoide posterior: braço a 60-90 graus, levemente atrás do plano frontal e em leve rotação interna; resistência em direção à flexão e adução.',
      'Registre sempre a mesma variação nas reavaliações.',
    ],
    predictN: ({ bmi, sexBinary }) => (bmi == null || sexBinary == null ? null : 107.24 + 2.17 * bmi - 66.72 * sexBinary),
  },
  {
    key: 'tricepsBrachii',
    label: 'Triceps brachii',
    shortLabel: 'Tríceps braquial',
    equationLabel: '208.51 - (2.16 x AGE) + (5.25 x BMI) - (92.06 x SEX)',
    description: [
      'Paciente em decúbito dorsal ou sentado/inclinado à frente, com o cotovelo totalmente fletido.',
      'O ombro pode ficar em cerca de 90 graus de abdução ou ao lado do corpo, com o úmero estabilizado.',
      'Solicite extensão completa do cotovelo contra a gravidade.',
      'A resistência é aplicada no antebraço distal, logo acima do punho, no sentido da flexão do cotovelo.',
    ],
    predictN: ({ ageYears, bmi, sexBinary }) =>
      ageYears == null || bmi == null || sexBinary == null ? null : 208.51 - 2.16 * ageYears + 5.25 * bmi - 92.06 * sexBinary,
  },
  {
    key: 'bicepsBrachii',
    label: 'Biceps brachii',
    shortLabel: 'Bíceps braquial',
    equationLabel: '135.16 + (7.16 x BMI) - (131.93 x SEX)',
    description: [
      'Paciente sentado, com o braço junto ao corpo, cotovelo a 90 graus e antebraço em supinação.',
      'Uma mão do examinador estabiliza o ombro ou o cotovelo para evitar compensação.',
      'Solicite flexão do cotovelo e diga ao paciente para não deixar o examinador estender o braço.',
      'A resistência é aplicada na face anterior do antebraço distal, logo acima do punho, no sentido da extensão do cotovelo.',
    ],
    predictN: ({ bmi, sexBinary }) => (bmi == null || sexBinary == null ? null : 135.16 + 7.16 * bmi - 131.93 * sexBinary),
  },
  {
    key: 'quadricepsFemoris',
    label: 'Quadriceps femoris',
    shortLabel: 'Quadríceps femoral',
    equationLabel: '197.52 + (11.88 x BMI) - (129.77 x SEX)',
    description: [
      'Paciente sentado com as coxas apoiadas e joelhos em flexão de aproximadamente 90 graus.',
      'O avaliador estabiliza a coxa distalmente.',
      'Solicite extensão do joelho elevando o tornozelo contra a resistência manual.',
      'A resistência é aplicada na face anterior da tíbia distal, mantendo CIVM por 6 segundos.',
    ],
    predictN: ({ bmi, sexBinary }) => (bmi == null || sexBinary == null ? null : 197.52 + 11.88 * bmi - 129.77 * sexBinary),
  },
] as const;

const PERIPHERAL_MUSCLE_STRENGTH_MAP = Object.fromEntries(
  PERIPHERAL_MUSCLE_STRENGTH_DEFINITIONS.map((definition) => [definition.key, definition]),
) as Record<PeripheralMuscleStrengthKey, PeripheralMuscleStrengthDefinition>;

export const getPeripheralMuscleDefinition = (key: PeripheralMuscleStrengthKey): PeripheralMuscleStrengthDefinition =>
  PERIPHERAL_MUSCLE_STRENGTH_MAP[key];

export const getPeripheralMuscleLabel = (key?: PeripheralMuscleStrengthKey | null): string =>
  key ? getPeripheralMuscleDefinition(key).shortLabel : '—';

export const getDefaultPeripheralMuscleSelection = (): PeripheralMuscleStrengthKey[] =>
  PERIPHERAL_MUSCLE_STRENGTH_DEFINITIONS.map((definition) => definition.key);

const sanitizeSelectedMuscles = (
  value: unknown,
  { fallbackToAll = false }: { fallbackToAll?: boolean } = {},
): PeripheralMuscleStrengthKey[] => {
  const validKeys = new Set<PeripheralMuscleStrengthKey>(getDefaultPeripheralMuscleSelection());
  const entries = Array.isArray(value) ? value : [];
  const selected = entries.filter(
    (entry, index): entry is PeripheralMuscleStrengthKey =>
      typeof entry === 'string' && validKeys.has(entry as PeripheralMuscleStrengthKey) && entries.indexOf(entry) === index,
  );

  if (!selected.length && fallbackToAll) {
    return getDefaultPeripheralMuscleSelection();
  }

  return selected;
};

export const buildPeripheralMuscleStrengthPayload = ({
  unit,
  dominantSide,
  selectedMuscles,
  attemptsByMuscle,
  notes = '',
  patientContext,
  finishedAt,
}: PeripheralMuscleStrengthBuildInput): PeripheralMuscleStrengthResultPayload => {
  const ageYears = toFiniteNumber(patientContext?.ageYears);
  const bmi = calculateBmi(patientContext?.weightKg, patientContext?.heightCm);
  const sexBinary = resolveSexBinary(patientContext?.sex);
  const equationContext: EquationContext = { ageYears, bmi, sexBinary };

  const muscles = PERIPHERAL_MUSCLE_STRENGTH_DEFINITIONS.reduce((accumulator, definition) => {
    const attemptsN = normalizeAttemptsToNewtons(attemptsByMuscle[definition.key], unit);
    accumulator[definition.key] = computeMeasurement(attemptsN, definition.predictN, equationContext);
    return accumulator;
  }, {} as Record<PeripheralMuscleStrengthKey, PeripheralMuscleStrengthMeasurement>);

  const normalizedSelection = sanitizeSelectedMuscles(selectedMuscles);
  const selectedDefinitions = PERIPHERAL_MUSCLE_STRENGTH_DEFINITIONS.filter(({ key }) => normalizedSelection.includes(key));
  const completedMuscles = selectedDefinitions.filter(
    ({ key }) => muscles[key].attemptsN.length >= PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL.minAttempts,
  ).length;
  const reproducibleMuscles = selectedDefinitions.filter(({ key }) => muscles[key].reproducible).length;
  const percentPredictedEntries = selectedDefinitions.flatMap(({ key }) => {
    const value = muscles[key].percentPredicted;
    return value != null ? [{ key, value }] : [];
  });
  const averagePercentPredicted =
    percentPredictedEntries.length > 0
      ? round(
          percentPredictedEntries.reduce((sum, entry) => sum + entry.value, 0) / percentPredictedEntries.length,
          ROUNDING_PERCENT_DECIMALS,
        )
      : null;
  const lowestPercentPredictedEntry = percentPredictedEntries.reduce<{ key: PeripheralMuscleStrengthKey; value: number } | null>(
    (currentLowest, entry) => {
      if (!currentLowest || entry.value < currentLowest.value) return entry;
      return currentLowest;
    },
    null,
  );

  return {
    unit,
    dominantSide,
    bmi,
    selectedMuscles: normalizedSelection,
    muscles,
    summary: {
      selectedMusclesCount: selectedDefinitions.length,
      completedMuscles,
      reproducibleMuscles,
      allMusclesCompleted: selectedDefinitions.length > 0 && completedMuscles === selectedDefinitions.length,
      allMusclesReproducible: selectedDefinitions.length > 0 && reproducibleMuscles === selectedDefinitions.length,
      averagePercentPredicted,
      lowestPercentPredicted: lowestPercentPredictedEntry?.value ?? null,
      lowestMuscleKey: lowestPercentPredictedEntry?.key ?? null,
      lowestMuscleLabel: lowestPercentPredictedEntry ? getPeripheralMuscleLabel(lowestPercentPredictedEntry.key) : null,
    },
    protocol: PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL,
    notes,
    finishedAt: finishedAt || new Date().toISOString(),
    patientContext: patientContext
      ? {
          birthDate: patientContext.birthDate ?? null,
          ageYears,
          sex: patientContext.sex ?? null,
          weightKg: normalizePatientWeightKg(patientContext.weightKg),
          heightCm: normalizePatientHeightCm(patientContext.heightCm),
          identifier: patientContext.identifier ?? null,
        }
      : null,
  };
};

export const normalizePeripheralMuscleStrengthPayload = (payload: unknown): PeripheralMuscleStrengthResultPayload | null => {
  if (!payload || typeof payload !== 'object') return null;
  const record = payload as Record<string, unknown>;
  const patientContext =
    record.patientContext && typeof record.patientContext === 'object'
      ? (record.patientContext as Record<string, unknown>)
      : null;
  const professionalContext =
    record.professionalContext && typeof record.professionalContext === 'object'
      ? (record.professionalContext as PeripheralMuscleStrengthResultPayload['professionalContext'])
      : undefined;
  const muscles =
    record.muscles && typeof record.muscles === 'object'
      ? (record.muscles as Partial<Record<PeripheralMuscleStrengthKey, Record<string, unknown>>>)
      : {};

  const unit: PeripheralForceUnit = record.unit === 'N' ? 'N' : 'kgf';
  const dominantSide: PeripheralMuscleStrengthSide = record.dominantSide === 'left' ? 'left' : 'right';
  const selectedMuscles = sanitizeSelectedMuscles(record.selectedMuscles, { fallbackToAll: true });

  const attemptsByMuscle = PERIPHERAL_MUSCLE_STRENGTH_DEFINITIONS.reduce((accumulator, definition) => {
    const musclePayload = muscles[definition.key];
    const attemptsN = normalizeAttemptsToNewtons(musclePayload?.attemptsN, 'N');
    if (attemptsN.length > 0) {
      accumulator[definition.key] = attemptsN;
      return accumulator;
    }

    const attemptsKgf = normalizeAttemptsToNewtons(musclePayload?.attemptsKgf, 'kgf');
    if (attemptsKgf.length > 0) {
      accumulator[definition.key] = attemptsKgf;
      return accumulator;
    }

    const legacyAttempts = normalizeAttemptsToNewtons(musclePayload?.attempts, unit);
    accumulator[definition.key] = legacyAttempts;
    return accumulator;
  }, {} as Partial<Record<PeripheralMuscleStrengthKey, number[]>>);

  const normalized = buildPeripheralMuscleStrengthPayload({
    unit,
    dominantSide,
    selectedMuscles,
    attemptsByMuscle,
    notes: typeof record.notes === 'string' ? record.notes : '',
    patientContext: {
      birthDate: typeof patientContext?.birthDate === 'string' ? patientContext.birthDate : null,
      ageYears: toFiniteNumber(patientContext?.ageYears),
      sex: typeof patientContext?.sex === 'string' ? patientContext.sex : null,
      weightKg: normalizePatientWeightKg(patientContext?.weightKg),
      heightCm: normalizePatientHeightCm(patientContext?.heightCm),
      identifier: typeof patientContext?.identifier === 'string' ? patientContext.identifier : null,
    },
    finishedAt: typeof record.finishedAt === 'string' ? record.finishedAt : undefined,
  });

  return {
    ...normalized,
    notes: typeof record.notes === 'string' ? record.notes : normalized.notes,
    finishedAt: typeof record.finishedAt === 'string' ? record.finishedAt : normalized.finishedAt,
    patientContext: patientContext ? (patientContext as PeripheralMuscleStrengthPatientContext) : normalized.patientContext,
    professionalContext,
  };
};

export const summarizePeripheralMuscleStrengthResult = (payload: PeripheralMuscleStrengthResultPayload | null | undefined) => {
  const normalized = normalizePeripheralMuscleStrengthPayload(payload);
  const selectedDefinitions = PERIPHERAL_MUSCLE_STRENGTH_DEFINITIONS.filter((definition) =>
    normalized?.selectedMuscles?.includes(definition.key),
  );
  const muscleEntries = selectedDefinitions.map((definition) => ({
    key: definition.key,
    label: definition.shortLabel,
    equationLabel: definition.equationLabel,
    helpDescription: definition.description,
    attemptsN: normalized?.muscles[definition.key]?.attemptsN ?? [],
    attemptsKgf:
      normalized?.muscles[definition.key]?.attemptsN.map((value) =>
        Number((convertForceValue(value, 'N', 'kgf') ?? 0).toFixed(ROUNDING_FORCE_DECIMALS)),
      ) ?? [],
    bestN: normalized?.muscles[definition.key]?.bestN ?? null,
    bestKgf: normalized?.muscles[definition.key]?.bestKgf ?? null,
    predictedN: normalized?.muscles[definition.key]?.predictedN ?? null,
    predictedKgf: normalized?.muscles[definition.key]?.predictedKgf ?? null,
    percentPredicted: normalized?.muscles[definition.key]?.percentPredicted ?? null,
    topTwoDiffPercent: normalized?.muscles[definition.key]?.topTwoDiffPercent ?? null,
    reproducible: normalized?.muscles[definition.key]?.reproducible ?? false,
    completed:
      (normalized?.muscles[definition.key]?.attemptsN.length ?? 0) >= PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL.minAttempts,
  }));

  return {
    unit: normalized?.unit ?? 'kgf',
    dominantSide: normalized?.dominantSide ?? 'right',
    dominantSideLabel: normalized?.dominantSide === 'left' ? 'Esquerdo' : 'Direito',
    bmi: normalized?.bmi ?? null,
    ageYears: toFiniteNumber(normalized?.patientContext?.ageYears),
    sex: resolvePeripheralSex(normalized?.patientContext?.sex),
    weightKg: normalizePatientWeightKg(normalized?.patientContext?.weightKg),
    heightCm: normalizePatientHeightCm(normalized?.patientContext?.heightCm),
    selectedMuscles: normalized?.selectedMuscles ?? [],
    selectedMusclesCount: normalized?.summary.selectedMusclesCount ?? 0,
    completedMuscles: normalized?.summary.completedMuscles ?? 0,
    reproducibleMuscles: normalized?.summary.reproducibleMuscles ?? 0,
    allMusclesCompleted: normalized?.summary.allMusclesCompleted ?? false,
    allMusclesReproducible: normalized?.summary.allMusclesReproducible ?? false,
    averagePercentPredicted: normalized?.summary.averagePercentPredicted ?? null,
    lowestPercentPredicted: normalized?.summary.lowestPercentPredicted ?? null,
    lowestMuscleKey: normalized?.summary.lowestMuscleKey ?? null,
    lowestMuscleLabel: normalized?.summary.lowestMuscleLabel ?? null,
    notes: normalized?.notes ?? '',
    protocol: normalized?.protocol ?? PERIPHERAL_MUSCLE_STRENGTH_PROTOCOL,
    muscles: muscleEntries,
  };
};

export const getPeripheralStrengthSeverityLabel = (payload?: PeripheralMuscleStrengthResultPayload | null): string => {
  return 'Modo exploratório — sem classificação';
};

export const getPeripheralStrengthSeverityTone = (payload?: PeripheralMuscleStrengthResultPayload | null): string => {
  return 'bg-amber-100 text-amber-800';
};

export const getPeripheralStrengthInterpretationText = (payload?: PeripheralMuscleStrengthResultPayload | null): string => {
  const summary = summarizePeripheralMuscleStrengthResult(payload);
  const incompleteCount = summary.muscles.filter((muscle) => !muscle.completed).length;
  const nonReproducibleCount = summary.muscles.filter((muscle) => muscle.completed && !muscle.reproducible).length;

  if (summary.selectedMusclesCount === 0) {
    return 'Nenhum grupo muscular foi selecionado para esta avaliação.';
  }

  if (!summary.muscles.some((muscle) => muscle.bestKgf != null)) {
    return 'Não há medidas válidas registradas para interpretar a avaliação de força muscular periférica.';
  }

  const lowestText =
    summary.lowestPercentPredicted != null && summary.lowestMuscleLabel
      ? `${summary.lowestMuscleLabel} apresentou a menor relação exploratória com o predito (${summary.lowestPercentPredicted.toFixed(1)}%).`
      : 'Não foi possível calcular a comparação com o predito para todos os grupos musculares.';
  const averageText =
    summary.averagePercentPredicted != null
      ? `A média dos grupos com referência disponível foi ${summary.averagePercentPredicted.toFixed(1)}% do predito.`
      : 'Algumas equações exigem idade, sexo e BMI para calcular o predito.';
  const reproducibilityText =
    incompleteCount > 0
      ? `Há ${incompleteCount} grupo(s) muscular(es) selecionado(s) sem o número mínimo de tentativas.`
      : summary.allMusclesReproducible
        ? 'Os dois maiores valores de todos os músculos selecionados diferiram menos de 5%.'
        : `O critério de reprodutibilidade (<${summary.protocol.reproducibilityThresholdPercent}% entre os 2 maiores valores) não foi atingido em ${nonReproducibleCount} músculo(s).`;

  return resolveAssessmentInterpretationText(
    payload,
    `${lowestText} ${averageText} ${reproducibilityText} As equações de Garcia et al. (2024) permanecem em validação de unidade e protocolo; o limite inferior esperado e a classificação de fraqueza estão indisponíveis.`,
  );
};
